import { useSyncExternalStore } from 'react';
import {
  Platform,
  StyleSheet,
  View as RNView,
  type AlertButton,
} from 'react-native';
import { YStack } from 'tamagui';

import { color, space } from '@/constants/tokens';
import { alertEmitter } from '@/src/utils/alertEmitter';
import PSButton from './PSButton';
import PSText from './PSText';

/**
 * PSAlertHost — where web's `showAlert()` calls actually render.
 *
 * `showAlert` hands an alert to `Alert.alert`, which has no web implementation,
 * so on web it queues onto `alertEmitter` instead. This is the other half: the
 * subscriber that draws the queue. Without it those calls go nowhere — which is
 * how Settings' Log out came to do nothing at all on web.
 *
 * One at a time, in order: `peek` reads the head and the pressed button shifts
 * it, so a second alert raised while one is up waits its turn instead of
 * replacing it. The shift happens BEFORE `onPress`, because a handler is free to
 * navigate away (Log out ends the session, which pops the protected route) and
 * must not leave its own alert behind on the way out.
 *
 * No backdrop press and no escape key: an alert with a destructive button is a
 * question, and dismissing it by missing the card is how that gets answered by
 * accident. Every path out is a button.
 */

// RN shows a lone "OK" for an alert raised without buttons — a notice rather
// than a question. Matched here, or those alerts would have no way out.
const DISMISS: AlertButton[] = [{ text: 'OK' }];

function variantFor(style: AlertButton['style']) {
  if (style === 'destructive') return 'destructive' as const;
  if (style === 'cancel') return 'outline' as const;
  return 'primary' as const;
}

export default function PSAlertHost() {
  const request = useSyncExternalStore(
    alertEmitter.subscribe,
    alertEmitter.peek,
    () => undefined
  );

  // Null off web: the emitter is only ever fed there.
  if (Platform.OS !== 'web' || !request) return null;

  const buttons = request.buttons?.length ? request.buttons : DISMISS;

  return (
    <RNView style={[StyleSheet.absoluteFill, styles.scrim]}>
      <YStack bg="$color.background" rounded="$base" p="$space.lg" gap="$lg">
        <YStack gap="$space.xs">
          <PSText.H2>{request.title}</PSText.H2>
          {request.message ? (
            <PSText.Body2>{request.message}</PSText.Body2>
          ) : null}
        </YStack>
        <YStack gap="$space.sm">
          {buttons.map((button) => (
            <PSButton
              key={button.text}
              title={button.text ?? 'OK'}
              fullWidth
              variant={variantFor(button.style)}
              onPress={() => {
                alertEmitter.shift();
                button.onPress?.();
              }}
            />
          ))}
        </YStack>
      </YStack>
    </RNView>
  );
}

const styles = StyleSheet.create({
  // Plain RN, composed with absoluteFill: pure positioning over the column, and
  // the values are read off the token module rather than hardcoded. (A <div>
  // isn't a replaced element, so unlike a <video> it does fill from insets.)
  scrim: {
    justifyContent: 'center',
    paddingHorizontal: space.xl,
    backgroundColor: color.scrim,
  },
});
